import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { entityRefs } from '../../util/genericHelpers';
import {
  getNonInquiryTransitionsForQuery,
  isInquiryTransaction,
} from '../../util/transactionInquiry';

// ================ Constants ================ //

const RESULT_PAGE_SIZE = 100;

// Action Types
export const ActionTypes = {
  FETCH_TRANSACTIONS_REQUEST: 'app/OrdersListViewPage/FETCH_TRANSACTIONS_REQUEST',
  FETCH_TRANSACTIONS_SUCCESS: 'app/OrdersListViewPage/FETCH_TRANSACTIONS_SUCCESS',
  FETCH_TRANSACTIONS_ERROR: 'app/OrdersListViewPage/FETCH_TRANSACTIONS_ERROR',
};

// ================ Reducer ================ //

const initialState = {
  fetchTransactionsInProgress: false,
  fetchTransactionsError: null,
  transactions: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ActionTypes.FETCH_TRANSACTIONS_REQUEST:
      return {
        ...state,
        fetchTransactionsInProgress: true,
        fetchTransactionsError: null,
        transactions: [],
      };
    case ActionTypes.FETCH_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        fetchTransactionsInProgress: false,
        fetchTransactionsError: null,
        transactions: payload,
      };
    case ActionTypes.FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchTransactionsInProgress: false,
        fetchTransactionsError: payload,
        transactions: [],
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //
export const getTransactions = state => state.OrdersListViewPage.transactions;

export const getFetchTransactionsInProgress = state =>
  state.OrdersListViewPage.fetchTransactionsInProgress;

// ================ Action Creators ================ //

export const fetchTransactionsRequest = () => ({
  type: ActionTypes.FETCH_TRANSACTIONS_REQUEST,
});

export const fetchTransactionsSuccess = data => ({
  type: ActionTypes.FETCH_TRANSACTIONS_SUCCESS,
  payload: data,
});

export const fetchTransactionsError = error => ({
  type: ActionTypes.FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: error,
});

export const fetchTransactions = params => async (dispatch, getState, sdk) => {
  const { config, page = 1, perPage = RESULT_PAGE_SIZE } = params;

  dispatch(fetchTransactionsRequest());

  try {
    const baseQuery = {
      prot_listingDeliveryType: 'digitalDelivery,physicalItem,liveSession',
      perPage,
      lastTransitions: getNonInquiryTransitionsForQuery(),
      include: [
        'listing',
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
      ],
      'fields.transaction': [
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
        'metadata',
        'protectedData.serviceOfferParams',
      ],
      'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    let transactions = [];
    let entities = {};
    let totalPages = 1;
    const CONCURRENCY = 10; // Safe parallel batch size for Sharetribe API

    // Fetch the starting page first to determine totalPages
    let currentPage = page;
    const firstResponse = await sdk.transactions.query({ ...baseQuery, page: currentPage });
    const firstApiData = firstResponse.data || {};

    const { meta, data: firstTxData = [] } = firstApiData;
    totalPages = meta?.totalPages ?? 1;

    // Accumulate entities and denormalise first page
    entities = updatedEntities(entities, firstApiData);
    const firstTxRefs = entityRefs(firstTxData);
    transactions.push(...denormalisedEntities(entities, firstTxRefs, false));

    // console.log(
    //   `Fetched page ${currentPage}/${totalPages}: ${firstTxData.length} transactions (total so far: ${transactions.length})`
    // );

    // If there are more pages, fetch the rest in parallel batches
    if (totalPages > currentPage) {
      const remainingPages = [];
      for (let p = currentPage + 1; p <= totalPages; p++) {
        remainingPages.push(p);
      }

      // Process in concurrent batches while preserving order
      for (let i = 0; i < remainingPages.length; i += CONCURRENCY) {
        const batchPages = remainingPages.slice(i, i + CONCURRENCY);
        const batchPromises = batchPages.map(p =>
          sdk.transactions.query({ ...baseQuery, page: p })
        );

        const batchResponses = await Promise.all(batchPromises);

        batchResponses.forEach((resp, idx) => {
          const pageNum = batchPages[idx];
          const apiData = resp.data || {};
          const { data: batchTxData = [] } = apiData;

          // Cumulatively update entities (critical for correct denormalisation)
          entities = updatedEntities(entities, apiData);

          const txRefs = entityRefs(batchTxData);
          const denormTx = denormalisedEntities(entities, txRefs, false);
          transactions.push(...denormTx);

          // console.log(
          //   `Fetched page ${pageNum}/${totalPages}: ${batchTxData.length} transactions (total so far: ${transactions.length})`
          // );
        });
      }
    }

    const nonInquiryTransactions = transactions.filter(tx => !isInquiryTransaction(tx));

    dispatch(fetchTransactionsSuccess(nonInquiryTransactions));
    return nonInquiryTransactions;
  } catch (error) {
    dispatch(fetchTransactionsError(storableError(error)));
    throw error;
  }
};

export const loadData = (params, search, config) => async dispatch => {
  // Dispatch both actions concurrently for faster initial load
  // (they are independent — no need to await one before the other)
  const transactionsPromise = dispatch(fetchTransactions({ config }));
  const userPromise = dispatch(fetchCurrentUser());

  // Return a combined promise (resolves when both complete)
  // This mirrors common patterns in route loaders / data fetching
  return Promise.all([transactionsPromise, userPromise]);
};
